import React, { useId } from 'react';
import type { Nullable } from 'corgi-types';

import { FieldChangeTrigger } from '../../../enums/form';
import type { FieldChangeTriggerHandler } from '../../../types/form';
import type { IntlTelInputChangeResult } from '../../../types/tel';
import { getHTMLElementName } from '../../../utils/form/name';
import IntlTelInput from '../../IntlTelInput';
import { FieldInfo } from '../FieldInfo';
import { getChangeHandler } from './utils';

interface StateProps {
  hasError?: boolean;
  hasWarning?: boolean;
  hasSuccess?: boolean;
  errors?: string[];
  warnings?: string[];
  successes?: string[];
}

export interface TelFieldProps<FieldName extends string = string> extends StateProps {
  label: string;
  name: FieldName;
  info: Nullable<string>;
  value?: string;
  onChange: FieldChangeTriggerHandler<FieldName>;
  required?: boolean;
  nameSuffix?: string;
}

const getStateProps = ({ errors, warnings, successes, ...restProps }: StateProps) => {
  const { hasError, hasWarning, hasSuccess } = restProps;

  return {
    ...restProps,
    ...(hasError && { errors }),
    ...(hasWarning && { warnings }),
    ...(hasSuccess && { successes }),
  };
};

const TelField = ({
  label,
  name,
  value,
  info,
  errors,
  hasError,
  warnings,
  hasWarning,
  successes,
  hasSuccess,
  required,
  onChange,
  nameSuffix,
  ...restProps
}: TelFieldProps) => {
  const id = 'intl-tel-input-' + useId();
  const stateProps = getStateProps({
    hasError,
    hasWarning,
    hasSuccess,
    errors,
    warnings,
    successes,
  });
  const handlerProps = { fieldName: name, onChange };
  const handleChange = (newValue: Nullable<IntlTelInputChangeResult>) =>
    getChangeHandler({
      ...handlerProps,
      value: newValue,
      trigger: FieldChangeTrigger.Change,
    });
  const handleBlur = (newValue: Nullable<IntlTelInputChangeResult>) =>
    getChangeHandler({
      ...handlerProps,
      value: newValue,
      trigger: FieldChangeTrigger.Blur,
    });

  return (
    <>
      <IntlTelInput
        id={id}
        name={getHTMLElementName(name, nameSuffix)}
        placeholder={label}
        label={label}
        initialValue={value}
        onChange={handleChange}
        onBlur={handleBlur}
        required={required ?? false}
        {...stateProps}
        {...restProps}
      />
      <FieldInfo info={info} />
    </>
  );
};

export default TelField;
